import { useState, useEffect, useCallback, useRef } from "react";
import { ImagePlus, Loader2, Sparkles, Download, Trash2, Wand2, Upload, RefreshCw, Info, Layers, Check, Car } from "lucide-react";
import { API, uploadFile, downloadFile, apiFetch } from "@/portal/api";
import { toast } from "sonner";
import { confirmDialog } from "@/portal/confirm";
import AttachToVehicleDialog from "@/portal/components/AttachToVehicleDialog";

const photoExt = (p) => (p?.content_type?.includes("webp") ? "webp" : p?.content_type?.includes("jpeg") ? "jpg" : "png");

const EXAMPLES = [
  "Dej auto na naše pozadí před garáž, přidej realistický stín.",
  "Odstraň registrační značku (SPZ) a vyčisti pozadí.",
  "Zarovnej auto do centra, mírně zepředu z levého boku.",
  "Zesvětli fotku a dolaď lesk laku, auto neměň.",
];

const PhotoStudioTab = ({ token }) => {
  const [items, setItems] = useState([]); // [{file, url}]
  const [instruction, setInstruction] = useState("");
  const mode = "auto";
  const [useBackground, setUseBackground] = useState(true);
  const [addLogo, setAddLogo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {done,total}
  const [result, setResult] = useState(null); // single {id}
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [attachIds, setAttachIds] = useState(null); // array of photo ids -> dialog open
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try { setPhotos(await apiFetch("/admin/photos", { token })); } catch (e) { /* ignore */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const onPick = (fileList) => {
    const arr = Array.from(fileList || []).filter((f) => f.size <= 15 * 1024 * 1024);
    if (arr.length !== (fileList?.length || 0)) toast.error("Některé soubory byly přeskočeny (max 15 MB).");
    if (!arr.length) return;
    items.forEach((it) => URL.revokeObjectURL(it.url));
    setItems(arr.map((f) => ({ file: f, url: URL.createObjectURL(f) })));
    setResult(null);
  };

  const isBatch = items.length > 1;

  const generate = async () => {
    if (!items.length) { toast.error("Nejprve nahrajte fotku auta."); return; }
    setBusy(true); setResult(null); setProgress(null);
    const fields = { instruction, use_background: useBackground ? "true" : "false", add_logo: addLogo ? "true" : "false", mode, draft: isBatch ? "false" : "true" };
    try {
      if (isBatch) {
        setProgress({ done: 0, total: items.length });
        let ok = 0;
        // Sequential on purpose: parallel bursts hit the AI rate limit. One at a time + backend retry = reliable.
        for (let i = 0; i < items.length; i++) {
          try {
            const rec = await uploadFile("/admin/photo/edit", items[i].file, token, fields);
            const res = await pollPhoto(rec.id);
            if (res.status === "done") ok++;
            else toast.error(`Fotka ${i + 1}: ${res.error || "nezdařilo se"}`);
          } catch (e) { toast.error(`Fotka ${i + 1}: ${e.message}`); }
          setProgress({ done: i + 1, total: items.length });
          load();
        }
        toast.success(`Hotovo – ${ok}/${items.length} fotek vytvořeno.`);
      } else {
        const rec = await uploadFile("/admin/photo/edit", items[0].file, token, fields);
        const done = await pollPhoto(rec.id);
        if (done.status === "done") { setResult({ id: rec.id, draft: true, content_type: done.content_type }); toast.success("Fotka je hotová – zkontrolujte náhled a uložte, nebo zahoďte."); }
        else toast.error(done.error || "AI se nepodařilo dokončit úpravu.");
      }
      load();
    } catch (e) {
      toast.error(e.message || "Úprava se nezdařila.");
    } finally { setBusy(false); setProgress(null); }
  };

  // Poll job status until done/error (avoids long-request proxy timeouts).
  const pollPhoto = async (id) => {
    const started = Date.now();
    while (Date.now() - started < 300000) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const doc = await apiFetch(`/admin/photos/${id}`, { token });
        if (doc.status && doc.status !== "processing") return doc;
      } catch (e) { /* keep polling */ }
    }
    return { status: "error", error: "Vypršel čas – zkuste to prosím znovu." };
  };

  const approveResult = async () => {
    if (!result) return;
    try {
      await apiFetch(`/admin/photos/${result.id}/approve`, { method: "POST", token });
      setResult({ ...result, draft: false }); toast.success("Fotka uložena do galerie."); load();
    } catch (e) { toast.error(e.message); }
  };
  const discardResult = async () => {
    if (!result) return;
    try { await apiFetch(`/admin/photos/${result.id}`, { method: "DELETE", token }); setResult(null); toast.info("Fotka zahozena."); } catch (e) { toast.error(e.message); }
  };

  const del = async (id) => {
    if (!(await confirmDialog({ title: "Smazat fotku?", description: "Tuto vygenerovanou fotku nelze obnovit." }))) return;
    try {
      await apiFetch(`/admin/photos/${id}`, { method: "DELETE", token });
      if (result?.id === id) setResult(null);
      setSelected((s) => s.filter((x) => x !== id));
      load();
    } catch (e) { toast.error(e.message); }
  };

  const donePhotos = photos.filter((p) => p.status === "done" || (!p.status && !p.error));

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = donePhotos.length > 0 && selected.length === donePhotos.length;
  const toggleSelectAll = () => setSelected(allSelected ? [] : donePhotos.map((p) => p.id));

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!(await confirmDialog({ title: `Smazat ${selected.length} fotek?`, description: "Vybrané fotky nelze obnovit." }))) return;
    try {
      const res = await apiFetch("/admin/photos/bulk-delete", { method: "POST", token, body: { ids: selected } });
      if (result && selected.includes(result.id)) setResult(null);
      toast.success(`Smazáno ${res.deleted} fotek.`);
      setSelected([]);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const resultSrc = result ? `${API}/admin/photos/${result.id}/file` : null;

  return (
    <div className="space-y-6" data-testid="photo-studio-tab">
      <div className="flex items-center gap-2">
        <Wand2 className="w-5 h-5 text-orange-400" />
        <h2 className="font-['Cabinet_Grotesk'] text-xl font-bold">AI Foto studio</h2>
      </div>
      <p className="text-sm text-zinc-400 -mt-3 max-w-2xl">
        Nahrajte fotku auta (nebo rovnou celou sadu). Špičkový AI model sám pozná exteriér i interiér: exteriér vsadí na profesionální firemní pozadí před garáž, interiér a detaily věrně vyčistí a doladí. Do pole níž můžete napsat, co ještě upravit.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: input */}
        <div className="glass rounded-2xl p-5 border border-white/10 space-y-4">
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => onPick(e.target.files)} data-testid="photo-file-input" />

          {items.length === 0 ? (
            <button onClick={() => inputRef.current?.click()}
              className="w-full rounded-xl border-2 border-dashed border-white/15 hover:border-orange-500/60 transition-colors py-12 flex flex-col items-center gap-2 text-zinc-400 hover:text-white"
              data-testid="photo-upload-dropzone">
              <ImagePlus className="w-8 h-8" />
              <span className="text-sm font-medium">Nahrát fotku auta (i více najednou)</span>
              <span className="text-xs text-zinc-600">JPG, PNG, WEBP · max 15 MB / kus</span>
            </button>
          ) : isBatch ? (
            <div>
              <div className="grid grid-cols-3 gap-2" data-testid="photo-batch-previews">
                {items.map((it, i) => (
                  <div key={i} className="relative rounded-lg overflow-hidden border border-white/10 aspect-square bg-black/30">
                    <img src={it.url} alt={`Foto ${i + 1}`} className="w-full h-full object-contain" />
                  </div>
                ))}
              </div>
              <button onClick={() => inputRef.current?.click()} className="text-xs text-orange-300 mt-2 inline-flex items-center gap-1.5" data-testid="photo-change-btn">
                <Upload className="w-3.5 h-3.5" /> Změnit výběr ({items.length} fotek)
              </button>
            </div>
          ) : (
            <div className="relative rounded-xl overflow-hidden border border-white/10">
              <img src={items[0].url} alt="Náhled auta" className="w-full max-h-72 object-contain bg-black/30" data-testid="photo-source-preview" />
              <button onClick={() => inputRef.current?.click()}
                className="absolute bottom-2 right-2 text-xs bg-black/70 hover:bg-black text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                data-testid="photo-change-btn"><Upload className="w-3.5 h-3.5" /> Změnit</button>
            </div>
          )}

          {mode && (
          <div>
            <label className="text-sm text-zinc-300 font-medium mb-1.5 block">Co má AI udělat? <span className="text-zinc-500 font-normal">(nepovinné)</span></label>
            <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3}
              placeholder="Např.: Přidej realistický stín, odstraň SPZ, zesvětli lak. Auto neměň."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500 resize-none"
              data-testid="photo-instruction-input" />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setInstruction(ex)}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-zinc-400 hover:text-orange-300 hover:border-orange-500/40 transition-colors"
                  data-testid="photo-example-chip">{ex}</button>
              ))}
            </div>
          </div>
          )}

          <label className="flex items-center gap-2.5 text-sm text-zinc-300 cursor-pointer select-none" data-testid="photo-usebg-toggle">
            <input type="checkbox" checked={useBackground} onChange={(e) => setUseBackground(e.target.checked)}
              className="w-4 h-4 accent-orange-500" />
            Použít firemní pozadí (garáž EffectDeal)
          </label>

          <label className="flex items-center gap-2.5 text-sm text-zinc-300 cursor-pointer select-none" data-testid="photo-logo-toggle">
            <input type="checkbox" checked={addLogo} onChange={(e) => setAddLogo(e.target.checked)}
              className="w-4 h-4 accent-orange-500" />
            Přidat logo EffectDeal (vpravo dole)
          </label>

          <button onClick={generate} disabled={busy || !items.length}
            className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="photo-generate-btn">
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {progress ? `Vytvářím ${progress.done}/${progress.total}…` : "Vytvářím… (~20–40 s)"}</>
            ) : isBatch ? (
              <><Layers className="w-4 h-4" /> Vytvořit celou sadu ({items.length})</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Vytvořit fotku</>
            )}
          </button>
          <p className="text-[11px] text-zinc-600 inline-flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Každá fotka se zpracuje podle svého typu: exteriér na firemní pozadí, interiér a detaily věrně vyčistí. Hotové fotky najdete v galerii níže.
          </p>
        </div>

        {/* Right: result */}
        <div className="glass rounded-2xl p-5 border border-white/10 flex flex-col">
          <h3 className="text-sm font-medium text-zinc-300 mb-3">Výsledek</h3>
          <div className="flex-1 flex items-center justify-center rounded-xl border border-white/10 bg-black/30 min-h-64 overflow-hidden">
            {busy ? (
              <div className="text-center text-zinc-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-orange-400" /><p className="text-sm">{progress ? `Zpracovávám ${progress.done}/${progress.total}…` : "AI pracuje na fotce…"}</p></div>
            ) : isBatch ? (
              <p className="text-sm text-zinc-600 px-6 text-center">Hotová sada se zobrazí v galerii níže.</p>
            ) : resultSrc ? (
              <img src={resultSrc} alt="Výsledek" className="w-full max-h-[28rem] object-contain" data-testid="photo-result-image" />
            ) : (
              <p className="text-sm text-zinc-600 px-6 text-center">Zde se zobrazí upravená fotka.</p>
            )}
          </div>
          {result?.draft && !busy && !isBatch && (
            <div className="mt-3 rounded-xl border border-orange-500/30 bg-orange-500/10 p-3" data-testid="photo-draft-actions">
              <p className="text-xs text-orange-200 mb-2">Náhled před uložením – auto musí být celé a bez zbytků pozadí. Uložit do galerie, nebo zahodit?</p>
              <div className="flex gap-2">
                <button onClick={approveResult} className="btn-accent flex-1 inline-flex items-center justify-center gap-2" data-testid="photo-approve-btn"><Check className="w-4 h-4" /> Uložit do galerie</button>
                <button onClick={discardResult} className="px-4 py-2 rounded-full border border-red-500/40 text-sm text-red-300 hover:bg-red-500/10 inline-flex items-center gap-2" data-testid="photo-discard-btn"><Trash2 className="w-4 h-4" /> Zahodit</button>
                <button onClick={generate} className="px-4 py-2 rounded-full border border-white/15 text-sm text-zinc-300 hover:text-white inline-flex items-center gap-2" data-testid="photo-regenerate-btn"><RefreshCw className="w-4 h-4" /> Znovu</button>
              </div>
            </div>
          )}
          {result && !result.draft && !busy && !isBatch && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => setAttachIds([result.id])}
                className="btn-accent flex-1 inline-flex items-center justify-center gap-2" data-testid="photo-attach-btn">
                <Car className="w-4 h-4" /> Vložit k vozu
              </button>
              <button onClick={() => downloadFile(`/admin/photos/${result.id}/file`, token, `effectdeal-foto-${result.id.slice(0, 6)}.${photoExt(result)}`)}
                className="px-4 py-2 rounded-full border border-white/15 text-sm text-zinc-300 hover:text-white inline-flex items-center gap-2" data-testid="photo-download-btn">
                <Download className="w-4 h-4" /> Stáhnout
              </button>
              <button onClick={generate}
                className="px-4 py-2 rounded-full border border-white/15 text-sm text-zinc-300 hover:text-white inline-flex items-center gap-2" data-testid="photo-regenerate-btn">
                <RefreshCw className="w-4 h-4" /> Znovu
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Gallery */}
      {donePhotos.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h3 className="text-sm font-medium text-zinc-300">Poslední vytvořené fotky</h3>
            <div className="flex items-center gap-2">
              <button onClick={toggleSelectAll}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 hover:text-white transition-colors"
                data-testid="photo-select-all-btn">
                {allSelected ? "Zrušit výběr" : "Vybrat vše"}
              </button>
              {selected.length > 0 && (
                <button onClick={() => setAttachIds(selected)}
                  className="text-xs px-3 py-1.5 rounded-full bg-orange-500/20 hover:bg-orange-500/40 text-orange-200 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="photo-bulk-attach-btn">
                  <Car className="w-3.5 h-3.5" /> Vložit vybrané k vozu ({selected.length})
                </button>
              )}
              {selected.length > 0 && (
                <button onClick={bulkDelete}
                  className="text-xs px-3 py-1.5 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-200 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="photo-bulk-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Smazat vybrané ({selected.length})
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-gallery">
            {donePhotos.map((p) => {
              const isSel = selected.includes(p.id);
              return (
              <div key={p.id} className={`relative group rounded-xl overflow-hidden border bg-black/30 aspect-square transition-colors ${isSel ? "border-orange-500 ring-2 ring-orange-500/50" : "border-white/10"}`}>
                <img src={`${API}/admin/photos/${p.id}/file`} alt={p.instruction || "Foto"} className="w-full h-full object-contain" loading="lazy" />
                <button onClick={() => toggleSelect(p.id)}
                  className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-md flex items-center justify-center border transition-colors ${isSel ? "bg-orange-500 border-orange-500 text-white" : "bg-black/50 border-white/30 text-transparent hover:border-white"}`}
                  data-testid={`photo-select-${p.id}`} title="Vybrat">
                  <Check className="w-4 h-4" />
                </button>
                <div className="absolute inset-x-0 bottom-0 p-1.5 flex gap-1 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setAttachIds([p.id])} title="Vložit k vozu"
                    className="flex-1 text-[11px] bg-orange-500/30 hover:bg-orange-500/60 text-white py-1 rounded inline-flex items-center justify-center gap-1" data-testid={`photo-attach-${p.id}`}><Car className="w-3 h-3" /></button>
                  <button onClick={() => downloadFile(`/admin/photos/${p.id}/file`, token, `effectdeal-foto-${p.id.slice(0, 6)}.${photoExt(p)}`)} title="Stáhnout"
                    className="flex-1 text-[11px] bg-white/10 hover:bg-white/20 text-white py-1 rounded inline-flex items-center justify-center gap-1"><Download className="w-3 h-3" /></button>
                  <button onClick={() => del(p.id)}
                    className="flex-1 text-[11px] bg-red-500/20 hover:bg-red-500/40 text-red-200 py-1 rounded inline-flex items-center justify-center gap-1" data-testid={`photo-delete-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
      <AttachToVehicleDialog open={!!attachIds} onClose={() => setAttachIds(null)} photoIds={attachIds || []} token={token}
        onDone={() => setSelected([])} />
    </div>
  );
};

export default PhotoStudioTab;
